import AgoraRTC, { IAgoraRTCClient, ICameraVideoTrack, IMicrophoneAudioTrack } from 'agora-rtc-sdk-ng';
import BeautyExtension, { BeautyEffectOptions, IBeautyProcessor } from 'agora-extension-beauty-effect';

import { IBeautyOptionsData } from 'common/interface';
import { createLiveStreamToken } from 'api/liveStream';
import { transformBeautyOptionsData } from 'helper';

class AgoraManager {
  private client?: IAgoraRTCClient;
  private audioTrack?: IMicrophoneAudioTrack;
  private videoTrack?: ICameraVideoTrack;
  private publishVideoTrack?: any;
  private processor?: IBeautyProcessor;
  private watermarkAnimationId?: number;
  private watermarkCleanup?: () => void;
  private watermarkVideoEl?: HTMLVideoElement;
  private watermarkCanvas?: HTMLCanvasElement;
  /**
   * Vị trí watermark hiện tại:
   * 0 = top-left | 1 = top-right | 2 = bottom-right | 3 = bottom-left
   */
  private watermarkPositionIndex = 0;
  public enabledBeauty = false;

  getCanvasDimensions(): { width: number; height: number } | null {
    if (!this.watermarkCanvas || this.watermarkCanvas.width === 0) return null;
    return { width: this.watermarkCanvas.width, height: this.watermarkCanvas.height };
  }

  async getClient() {
    if (this.client) return this.client;
    const client = AgoraRTC.createClient({
      mode: 'live',
      codec: 'vp8',
    });
    const extension = new BeautyExtension();
    AgoraRTC.registerExtensions([extension]);

    const processor = extension.createProcessor();

    this.client = client;
    this.processor = processor;

    await client.setClientRole('host');
    client.on('token-privilege-will-expire', async () => {
      try {
        const {
          data: { token },
        } = await createLiveStreamToken();
        client.renewToken(token);
      } catch {}
    });
    client.on('user-published', async (user, mediaType) => {
      // Subscribe to a remote user.
      await client.subscribe(user, mediaType);
      // If the subscribed track is video.
      if (mediaType === 'video') {
        const remoteVideoTrack = user.videoTrack;
        const playerContainer: any = document.getElementById('stream_container');
        remoteVideoTrack?.play(playerContainer, { fit: 'contain', mirror: false });
      }

      // If the subscribed track is audio.
      if (mediaType === 'audio') {
        const remoteAudioTrack = user.audioTrack;
        remoteAudioTrack?.play();
      }
    });

    return client;
  }
  async getAudioTrack() {
    if (this.audioTrack) return this.audioTrack;
    const audioTrack = await AgoraRTC.createMicrophoneAudioTrack();
    this.audioTrack = audioTrack;
    return audioTrack;
  }
  async getVideoTrack() {
    if (this.videoTrack) return this.videoTrack;
    const videoTrack = await AgoraRTC.createCameraVideoTrack();
    this.videoTrack = videoTrack;
    return videoTrack;
  }

  async disableVideoTrack() {
    const activeTrack = this.publishVideoTrack || this.videoTrack;
    const nextMuted = !(activeTrack?.muted ?? this.videoTrack?.muted);

    if (this.videoTrack) {
      await this.videoTrack.setMuted(nextMuted);
    }

    if (this.publishVideoTrack && this.publishVideoTrack !== this.videoTrack) {
      await this.publishVideoTrack.setMuted(nextMuted);
    }
  }

  getStatusOfVideoTract() {
    return !(this.publishVideoTrack || this.videoTrack)?.muted;
  }
  async reloadCameraBeautyEffect() {
    if (this.processor && this.videoTrack) {
      this.videoTrack.pipe(this.processor).pipe(this.videoTrack.processorDestination);
    }
  }

  private cleanupWatermarkResources() {
    this.watermarkCleanup?.();
    this.watermarkCleanup = undefined;
    this.watermarkVideoEl?.remove();
    this.watermarkVideoEl = undefined;
    this.watermarkCanvas?.remove();
    this.watermarkCanvas = undefined;
  }

  /**
   * Canvas watermark: logo cố định góc trái dưới.
   * Cập nhật canvas dynamically theo video resolution thực tế (khắc phục bị đen video).
   * Dùng Web Worker để không crash/lag khi switch tab.
   * Fallback Safari: createImageBitmap -> Image, OffscreenCanvas -> Canvas
   */
  async createWatermarkedVideoTrack(logoUrl: string): Promise<any> {
    this.cleanupWatermarkResources();
    const sourceVideoTrack = await this.getVideoTrack();

    // Play chính local video track vào thẻ video ẩn để lấy frame sau khi đã qua beauty processor.
    const videoEl = document.createElement('video');
    Object.assign(videoEl, { autoplay: true, muted: true, playsInline: true });
    videoEl.style.cssText = 'position:fixed;top:-9999px;left:-9999px;pointer-events:none;';
    document.body.appendChild(videoEl);
    this.watermarkVideoEl = videoEl;

    sourceVideoTrack.play(videoEl, { fit: 'contain', mirror: false });

    // Chờ local player sẵn sàng để canvas lấy đúng frame đã qua processor.
    await new Promise<void>(res => {
      videoEl.onloadedmetadata = () => res();
      videoEl.onplaying = () => res();
      setTimeout(res, 2000);
    });

    // Fallback load ảnh cho Safari
    const img = new Image();
    try {
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = logoUrl;
      });
    } catch(e) {
      console.warn('[Watermark] Logo load failed:', e);
    }

    // Khởi tạo Canvas với kích thước thực tế của camera (đã có sau loadedmetadata)
    const canvas = document.createElement('canvas');
    canvas.width = videoEl.videoWidth > 0 ? videoEl.videoWidth : 1280;
    canvas.height = videoEl.videoHeight > 0 ? videoEl.videoHeight : 720;
    canvas.style.cssText = 'position:fixed;top:-9999px;left:-9999px;pointer-events:none;';
    document.body.appendChild(canvas);
    this.watermarkCanvas = canvas;
    const ctx = canvas.getContext('2d')!;

    // Đếm tick để đổi vị trí watermark (Worker chạy 15fps)
    // Mỗi interval là ngẫu nhiên 1–5 phút, tính lại sau mỗi lần đổi
    const getRandomTicks = () => Math.round((Math.random() * 4 + 1) * 60 * 15); // 1–5 phút
    let tickCount = 0;
    let nextChangeTick = getRandomTicks();

    const doFrame = () => {
      tickCount++;

      // Mỗi nextChangeTick tick thì đổi vị trí watermark
      if (tickCount >= nextChangeTick) {
        tickCount = 0;
        nextChangeTick = getRandomTicks(); // random interval mới
        let next: number;
        do {
          next = Math.floor(Math.random() * 4);
        } while (next === this.watermarkPositionIndex);
        this.watermarkPositionIndex = next;
        console.log(`[Watermark] Position -> ${next} (0=top-left,1=top-right,2=bottom-right,3=bottom-left)`);
      }

      // Phải có kích thước video thực tế thì mới render (tránh render màn hình đen/kéo dãn)
      if (videoEl.readyState >= 2 && videoEl.videoWidth > 0 && videoEl.videoHeight > 0) {
        const vw = videoEl.videoWidth;
        const vh = videoEl.videoHeight;
        
        // Auto resize canvas theo camera thực tế
        if (canvas.width !== vw || canvas.height !== vh) {
          canvas.width = vw;
          canvas.height = vh;
          console.log(`[Watermark] Resized canvas to ${vw}x${vh}`);
        }

        // Vẽ luồng camera đã mirror (để recording và live view nhất quán)
        ctx.save();
        ctx.translate(vw, 0);
        ctx.scale(-1, 1);
        ctx.drawImage(videoEl, 0, 0, vw, vh);
        ctx.restore();

        // Vẽ watermark theo vị trí hiện tại
        if (img.complete && img.naturalWidth > 0) {
          const logoW = Math.round(vw * 0.12);
          const logoH = Math.round(logoW / 3);
          const insetX = Math.round(vw * 0.06);
          const insetY = Math.round(vh * 0.06);

          // 4 vị trí: 0=top-left, 1=top-right, 2=bottom-right, 3=bottom-left
          let logoX: number;
          let logoY: number;
          const pos = this.watermarkPositionIndex;
          if (pos === 0) {
            logoX = insetX;
            logoY = insetY;
          } else if (pos === 1) {
            logoX = vw - logoW - insetX;
            logoY = insetY;
          } else if (pos === 2) {
            logoX = vw - logoW - insetX;
            logoY = vh - logoH - insetY;
          } else {
            logoX = insetX;
            logoY = vh - logoH - insetY;
          }

          ctx.save();
          ctx.globalAlpha = 1.0;
          ctx.drawImage(img, logoX, logoY, logoW, logoH);
          ctx.restore();
        }
      }
    };

    // Dùng Web Worker làm timer thay vì setInterval ở main thread
    // Chrome sẽ KHÔNG throttle Web Worker khi tab bị ẩn -> Stream mượt 15fps
    const workerCode = `
      let intervalId = null;
      self.onmessage = function(e) {
        if (e.data.command === 'start') {
          intervalId = setInterval(() => self.postMessage('tick'), e.data.interval);
        } else if (e.data.command === 'stop') {
          clearInterval(intervalId);
        }
      };
    `;
    const blob = new Blob([workerCode], { type: 'application/javascript' });
    const worker = new Worker(URL.createObjectURL(blob));

    worker.onmessage = () => {
      doFrame();
    };

    worker.postMessage({ command: 'start', interval: 1000 / 15 }); // Force 15 fps

    this.watermarkCleanup = () => {
      this.watermarkPositionIndex = 0;
      worker.postMessage({ command: 'stop' });
      worker.terminate();
    };

    const canvasStream = (canvas as any).captureStream(15);
    return AgoraRTC.createCustomVideoTrack({ mediaStreamTrack: canvasStream.getVideoTracks()[0] });
  }

  async getPublishVideoTrack({
    enableWatermark = false,
    logoUrl,
  }: {
    enableWatermark?: boolean;
    logoUrl?: string;
  } = {}) {
    const rawVideoTrack = await this.getVideoTrack();
    if (!enableWatermark || !logoUrl) {
      this.cleanupWatermarkResources();
      this.publishVideoTrack = rawVideoTrack;
      return rawVideoTrack;
    }

    try {
      const watermarkedTrack = await this.createWatermarkedVideoTrack(logoUrl);
      this.publishVideoTrack = watermarkedTrack;
      return watermarkedTrack;
    } catch (error) {
      console.warn('[Watermark] Fallback to raw track:', error);
      this.publishVideoTrack = rawVideoTrack;
      return rawVideoTrack;
    }
  }

  async getAudioAndVideoTrack(beautyOptions?: IBeautyOptionsData) {
    const audioTrack = await this.getAudioTrack();
    const videoTrack = await this.getVideoTrack();

    if (beautyOptions?.beautyEffect) {
      await this.enableBeauty();
    }
    if (beautyOptions) {
      await this.changeProcessorOptions(transformBeautyOptionsData(beautyOptions));
    }

    return [audioTrack, videoTrack];
  }

  async enableBeauty() {
    if (this.processor) {
      await this.processor.enable();
      if (this.videoTrack) {
        await this.reloadCameraBeautyEffect();
      }
      this.enabledBeauty = true;
    }
  }
  async disableBeauty() {
    if (this.processor) {
      await this.processor.disable();
      if (this.videoTrack) {
        await this.reloadCameraBeautyEffect();
      }
      this.enabledBeauty = false;
    }
  }
  async changeProcessorOptions(options: BeautyEffectOptions) {
    if (options && this.processor) {
      await this.processor.setOptions(options);
      if (this.videoTrack) {
        await this.reloadCameraBeautyEffect();
      }
    }
  }

  async stop() {
    this.cleanupWatermarkResources();
    if (this.watermarkAnimationId !== undefined) {
      cancelAnimationFrame(this.watermarkAnimationId);
      this.watermarkAnimationId = undefined;
    }
    this.client?.leave();
    this.client = undefined;
    if (this.publishVideoTrack && this.publishVideoTrack !== this.videoTrack) {
      this.publishVideoTrack.close();
    }
    this.publishVideoTrack = undefined;
    this.audioTrack?.close();
    this.videoTrack?.close();
    this.audioTrack = undefined;
    this.videoTrack = undefined;
    this.processor?.disable();
    this.processor = undefined;
    this.enabledBeauty = false;
  }
}
const agoraManager = new AgoraManager();
export const agoraManager2 = new AgoraManager();

export default agoraManager;
